import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DocumentPushConfirmationDialog from '@/components/documents/DocumentPushConfirmationDialog';
import { toaster } from '@/components/ui/toaster';
import { useAuth } from '@/context/AuthContext';
import { useCurrentHouse } from '@/context/CurrentHouseContext';
import { useHousePermissions } from '@/hooks/useHasPermission';
import { usePendingDocumentsToShare } from '@/hooks/documents/usePendingDocumentsToShare';
import { useShareDocumentsWithOwner } from '@/hooks/documents/useShareDocumentsWithOwner';
import useUploadedRecords, { UploadedRecord } from '@/hooks/house/documents/useUploadedRecords';
import { getLastTransferAt } from '@/utils/houseUtils';
import { isCriticalInfoUpload } from '@/utils/uploadItemUtils';
import { isArchivedProperty } from '@/utils/properties/propertyGroupingUtils';

interface DocumentPushPromptProps {
	/**
	 * When the host page already computes the post-transfer share queue (the Records page does, for
	 * bulk share), pass it in so we don't run usePendingDocumentsToShare — and its per-record scan —
	 * a second time. Omit it on pages that don't already have it (dashboard, maintenance); the prompt
	 * then loads records and computes the queue itself.
	 */
	pendingDocsToShare?: UploadedRecord[];
	isPendingSharesLoading?: boolean;
}

interface DocumentPushPromptInnerProps {
	houseId: number;
	pendingDocsToShare: UploadedRecord[];
	isPendingSharesLoading: boolean;
}

/**
 * Auto-prompts builders/professionals to send newly added post-transfer records to the current owner.
 * Mounted at page level so records added through the shared composer/dropzone trigger outside Records too.
 */
const DocumentPushPrompt: React.FC<DocumentPushPromptProps> = ({ pendingDocsToShare, isPendingSharesLoading }) => {
	const { house } = useCurrentHouse();
	const { user } = useAuth();
	const { canProposeDocument } = useHousePermissions();
	const isArchiveCopy = isArchivedProperty(house);
	const canShareDocuments = !!user && canProposeDocument;
	const enabled = canShareDocuments && isArchiveCopy;
	const lastTransferAt = useMemo(() => getLastTransferAt(house), [house]);

	if (!enabled) return null;

	if (pendingDocsToShare) {
		return (
			<DocumentPushPromptInner
				houseId={house.id}
				pendingDocsToShare={pendingDocsToShare}
				isPendingSharesLoading={isPendingSharesLoading ?? false}
			/>
		);
	}

	return <DocumentPushPromptRecordLoader houseId={house.id} lastTransferAt={lastTransferAt} />;
};

const DocumentPushPromptRecordLoader: React.FC<{ houseId: number; lastTransferAt: string | null }> = (props) => {
	const { houseId, lastTransferAt } = props;
	const { uploadItems } = useUploadedRecords(houseId);
	const documentUploadItems = useMemo(() => uploadItems.filter((item) => !isCriticalInfoUpload(item)), [uploadItems]);
	const { pendingDocsToShare, isPendingSharesLoading } = usePendingDocumentsToShare(
		houseId,
		documentUploadItems,
		lastTransferAt,
		true,
	);

	return (
		<DocumentPushPromptInner
			houseId={houseId}
			pendingDocsToShare={pendingDocsToShare}
			isPendingSharesLoading={isPendingSharesLoading}
		/>
	);
};

const DocumentPushPromptInner: React.FC<DocumentPushPromptInnerProps> = (props) => {
	const { houseId, pendingDocsToShare, isPendingSharesLoading } = props;
	const promptedStorageKey = `records-owner-send-prompted:${houseId}`;
	const promptedDocumentIdsRef = useRef<Set<number>>(new Set());

	const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
	const [promptDocumentIds, setPromptDocumentIds] = useState<number[]>([]);
	const [selectedPromptDocumentIds, setSelectedPromptDocumentIds] = useState<Set<number>>(new Set());
	// Covers the send POST so the Send button holds its loading state until the dialog closes; the
	// sent-shares refetch runs in the background (see useShareDocumentsWithOwner), so we don't wait on it.
	const [isSending, setIsSending] = useState(false);

	const { shareDocuments } = useShareDocumentsWithOwner(houseId);

	const promptDocuments = useMemo(() => {
		const promptIds = new Set(promptDocumentIds);
		return pendingDocsToShare.filter((record) => promptIds.has(record.document.id));
	}, [pendingDocsToShare, promptDocumentIds]);

	useEffect(() => {
		if (typeof window === 'undefined') return;

		try {
			const storedIds = JSON.parse(window.sessionStorage.getItem(promptedStorageKey) ?? '[]');
			promptedDocumentIdsRef.current = new Set(
				Array.isArray(storedIds) ? storedIds.filter((id): id is number => typeof id === 'number') : [],
			);
		} catch {
			promptedDocumentIdsRef.current = new Set();
		}
	}, [promptedStorageKey]);

	const markDocumentsPrompted = useCallback(
		(documentIds: number[]) => {
			documentIds.forEach((documentId) => promptedDocumentIdsRef.current.add(documentId));
			if (typeof window === 'undefined') return;

			window.sessionStorage.setItem(promptedStorageKey, JSON.stringify([...promptedDocumentIdsRef.current]));
		},
		[promptedStorageKey],
	);

	useEffect(() => {
		if (isPendingSharesLoading || isConfirmDialogOpen) return;

		const promptableIds = pendingDocsToShare
			.map((record) => record.document.id)
			.filter((documentId) => !promptedDocumentIdsRef.current.has(documentId));

		if (promptableIds.length > 0) {
			markDocumentsPrompted(promptableIds);
			setPromptDocumentIds(promptableIds);
			setSelectedPromptDocumentIds(new Set(promptableIds));
			setIsConfirmDialogOpen(true);
		}
	}, [isPendingSharesLoading, pendingDocsToShare, isConfirmDialogOpen, markDocumentsPrompted]);

	const closePrompt = () => {
		setPromptDocumentIds([]);
		setSelectedPromptDocumentIds(new Set());
		setIsConfirmDialogOpen(false);
	};

	const handleConfirmProceed = async () => {
		const idsToSend = [...selectedPromptDocumentIds];
		if (idsToSend.length === 0 || isSending) return;

		setIsSending(true);
		try {
			await shareDocuments(idsToSend);
			closePrompt();
		} catch {
			toaster.create({ title: 'Failed to send records to owner.', type: 'error' });
		} finally {
			setIsSending(false);
		}
	};

	if (isPendingSharesLoading) return null;

	return (
		<DocumentPushConfirmationDialog
			isOpen={isConfirmDialogOpen}
			onOpenChange={(open) => {
				if (open) {
					setIsConfirmDialogOpen(true);
				} else {
					closePrompt();
				}
			}}
			onConfirm={handleConfirmProceed}
			onCancel={closePrompt}
			isLoading={isSending}
			pendingDocuments={promptDocuments.map((record) => record.document)}
			selectedDocumentIds={selectedPromptDocumentIds}
			onDocumentSelectedChange={(documentId, selected) => {
				setSelectedPromptDocumentIds((previous) => {
					const next = new Set(previous);
					if (selected) {
						next.add(documentId);
					} else {
						next.delete(documentId);
					}
					return next;
				});
			}}
		/>
	);
};

export default DocumentPushPrompt;
